import { Button } from "@/components/design-system/button";
import FileUploadSection, {
  FileUploadSectionRef,
} from "@/modules/Assignments/components/FileUploadSection";
import { uploadFileService } from "@/service/base.service";
import { addQueryService } from "@/service/feedback.service";
import { buildQueryAddPayload } from "@/types/feedback.model";
import React, { useRef } from "react";
import { useParams } from "react-router";

function CreateQuery({
  onRefresh,
  selectedAssignmentType = "general",
  selectedLessonId,
  selectedUnitId,
  recordType = "course",
}: {
  onRefresh?: () => void;
  selectedAssignmentType?: string;
  selectedStudentId?: string;
  selectedLessonId?: string;
  selectedUnitId?: string;
  recordType?: "course" | "class";
}) {
  const { id } = useParams<{ id: string }>();

  const [noteText, setNoteText] = React.useState("");
  const [uploadedFile, setUploadedFile] = React.useState<File | null>(null);
  const [loading, setLoading] = React.useState(false);

  const fileUploadRef = useRef<FileUploadSectionRef>(null);

  const handleFileUpload = (file: File | null) => {
    if (!file) {
      setUploadedFile(null);
      return;
    }

    setUploadedFile(file);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const uploads: string[] = [];

      if (uploadedFile) {
        const {
          data: {
            data: { id: fileId },
          },
        } = await uploadFileService(uploadedFile as File);

        uploads.push(fileId);
      }

      const payload = buildQueryAddPayload({
        recordType,
        resourceId: id as string,
        assignmentType: selectedAssignmentType,
        notes: noteText,
        uploads,
        lessonId: selectedLessonId,
        unitId: selectedUnitId,
      });

      await addQueryService(payload);
      if (onRefresh) onRefresh();

      fileUploadRef.current?.clearSelectedFile();
      fileUploadRef.current?.clearSelectedRecordedFile();
      setNoteText("");
      setUploadedFile(null);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border rounded-b-xl border-[#EEEEEE]">
      <div className="h-[50px] px-[24px] py-[12px] border-b border-[#EEEEEE] flex items-center justify-between">
        <h3 className="text-xl font-semibold text-[#1F1F1F]">
          Add New Submission
        </h3>
      </div>

      <div className="px-6 pt-6">
        <FileUploadSection
          ref={fileUploadRef}
          onFileUpload={handleFileUpload}
          showLabel={false}
          disabled={loading}
        />

        <div className="mb-4">
          <p className="text-base font-medium text-[#2D3748] mb-2">
            Write a note
          </p>
          <textarea
            className="w-full border border-gray-200 rounded-2xl p-3 min-h-[100px] text-base text-[#1F1F1F] focus:outline-none"
            placeholder="Enter a note"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            disabled={loading}
          ></textarea>
        </div>
      </div>

      <div className="px-6 py-4.5 border-t border-[#EEEEEE]">
        <div className="flex items-center justify-between flex-wrap sm:flex-nowrap gap-2">
          <div className="flex items-center">
            <p className="text-xs text-[#1C1B1F] ">
              <span className="font-bold ">Note:</span> Upload MP4, MOV file (Up
              to 50 MB) / Upload MP3 file (Up to 10mb)/ PDF, DOCX
            </p>
          </div>

          <div className="flex items-center gap-2 sm:gap-4 flex-wrap sm:flex-nowrap">
            <Button
              variant={"primary"}
              className="h-[40px] w-[152px] rounded-[12px] bg-[#5C88C5]"
              disabled={!noteText}
              onClick={handleSubmit}
              loading={loading}
            >
              Add Submission
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreateQuery;
